import { useEffect, useState } from 'react';
import { FaUndo } from 'react-icons/fa';

import Error from '../Error';
import Loader from '../Loader';
import Pagination from '../Pagination';
import { tableBackgroundColor } from '../utils/tableBackgroundColor';

import { GlobalNotificationError } from '@/exceptions/global-notification/global-notification.enum';
import { useGetAllUsers } from '@/hooks/users/useGetAllUsers';
import { userService } from '@/services/user.service';

interface UserTableProps {
	selectedUsers: number[];
	setSelectedUsers: (newSelectedUsers: number[]) => void;
	usersError: string | null;
	setUsersError: (error: string | null) => void;
	allUsersAreSelected: boolean;
	setAllUsersAreSelected: React.Dispatch<React.SetStateAction<boolean>>;
}

const UserTable = ({
	selectedUsers,
	setSelectedUsers,
	usersError,
	setUsersError,
	allUsersAreSelected,
	setAllUsersAreSelected,
}: UserTableProps) => {
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSelectedUsers, setPageSelectedUsers] = useState<{
		[page: number]: Set<number>;
	}>({});
	const [loading, setLoading] = useState(false);
	const { users, isLoading, isError, error, isPending, totalPages } =
		useGetAllUsers(currentPage, 8);

	const handleError = () => {
		if (selectedUsers.length === 0) {
			setUsersError('No users selected');
			return;
		}
		setUsersError(null);
	};

	useEffect(() => {
		handleError();
	});

	useEffect(() => {
		const allSelectedUsers = Object.values(pageSelectedUsers).reduce(
			(acc, pageSet) => {
				pageSet.forEach((userId) => acc.add(userId));
				return acc;
			},
			new Set<number>(),
		);

		setSelectedUsers(Array.from(allSelectedUsers));
	}, [pageSelectedUsers]);

	if (isLoading || isPending) {
		return <Loader />;
	}

	if (isError) {
		return (
			<Error
				message={error ? error.message : GlobalNotificationError.USERS_ERROR}
			/>
		);
	}

	const handleSelectUser = (userId: number) => {
		setPageSelectedUsers((prev) => {
			const newSelectedUsersPerPage = { ...prev };

			if (!newSelectedUsersPerPage[currentPage]) {
				newSelectedUsersPerPage[currentPage] = new Set();
			}

			const selectedPageUsers = newSelectedUsersPerPage[currentPage];

			if (selectedPageUsers.has(userId)) {
				selectedPageUsers.delete(userId);
			} else {
				selectedPageUsers.add(userId);
			}

			return newSelectedUsersPerPage;
		});
	};

	const handleSelectPage = () => {
		setPageSelectedUsers((prev) => {
			const newSelectedUsersPerPage = { ...prev };
			if (!newSelectedUsersPerPage[currentPage]) {
				newSelectedUsersPerPage[currentPage] = new Set();
			}
			const selectedPageUsers = newSelectedUsersPerPage[currentPage];

			if (selectedPageUsers.size === users.length) {
				newSelectedUsersPerPage[currentPage] = new Set();
			} else {
				const validUsers = users.filter((user) => user.id !== undefined);
				const usersIds = validUsers.map((user) => user.id as number);
				usersIds.forEach((id) => selectedPageUsers.add(id));
			}

			return newSelectedUsersPerPage;
		});
	};

	const handleSelectAllUsers = async (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
	) => {
		e.stopPropagation();
		e.preventDefault();
		setLoading(true);
		try {
			const usersIds = await userService.getAllUsersIds();
			setSelectedUsers(usersIds);
			setAllUsersAreSelected(true);
		} catch (error) {
			console.error('Error selecting all users:', error);
		} finally {
			setLoading(false);
		}
	};

	const handleResetSelectedUsers = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
	) => {
		e.stopPropagation();
		e.preventDefault();
		setLoading(true);
		setSelectedUsers([]);
		setAllUsersAreSelected(false);
		setLoading(false);
	};

	return (
		<div className="w-[100%]">
			<div className="flex items-center justify-between w-full">
				<div>
					<h1
						data-test="h1-users"
						className="text-base font-semibold text-gray-900 w-full"
					>
						Select users to notify:
					</h1>
				</div>
				<div>
					{allUsersAreSelected ? (
						<button
							className="bg-red-600 rounded-md px-3 py-2 text-white"
							onClick={(e) => handleResetSelectedUsers(e)}
							disabled={loading}
						>
							<FaUndo />
						</button>
					) : (
						<button
							className="bg-blue-600 rounded-md px-4 py-1 text-white"
							onClick={(e) => handleSelectAllUsers(e)}
							disabled={loading}
						>
							Select all users
						</button>
					)}
				</div>
			</div>
			<div className="mt-8">
				<div className=" overflow-x-auto  overflow-y-auto">
					<div className="inline-block min-w-full  align-middle sm:px-2 ">
						<table
							className={`min-w-full divide-y divide-gray-300 ${
								allUsersAreSelected ? 'pointer-events-none opacity-50' : ''
							}`}
						>
							<thead className="bg-gray-300 sticky top-0 z-10">
								<tr>
									<th
										scope="col"
										className="py-3.5   text-center text-sm font-semibold text-gray-900 "
										data-test="th-id"
									>
										Id
									</th>
									<th
										scope="col"
										className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900"
										data-test="th-name"
									>
										Name
									</th>
									<th
										scope="col"
										className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900"
										data-test="th-email"
									>
										Email
									</th>
									<th
										scope="col"
										className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900"
										data-test="th-role"
									>
										Role
									</th>
									<th
										scope="col"
										className="flex items-center justify-center relative py-3.5 pl-3 pr-4 sm:pr-0"
										data-test="th-select"
									>
										<input
											type="checkbox"
											className="h-4 w-4"
											onChange={handleSelectPage}
											checked={
												selectedUsers.length === users.length &&
												users.length > 0
											}
										/>
									</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-gray-200">
								{users.map((person, index) => (
									<tr
										key={person.username}
										className={`${tableBackgroundColor(
											index,
										)} hover:bg-gray-200 min-h-8`}
									>
										<td className="py-3.5 pl-3 pr-3  whitespace-nowrap text-sm text-center font-medium text-gray-900 ">
											{person.id}
										</td>
										<td className="whitespace-nowrap px-3 py-4 text-center text-sm text-gray-500">
											{person.firstName} {person.lastName}
										</td>
										<td className="whitespace-nowrap px-3 py-4 text-center text-sm text-gray-500">
											{person.username}
										</td>
										<td className="whitespace-nowrap px-3 py-4 text-center text-sm text-gray-500">
											{person.roles[0]}
										</td>
										<td className="relative flex items-center justify-center whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-0">
											<a
												href="#"
												className="text-indigo-600 hover:text-indigo-900"
											>
												<input
													type="checkbox"
													onChange={() => handleSelectUser(person.id as number)}
													checked={selectedUsers.includes(person.id as number)}
												></input>
											</a>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			</div>
			<p className={`text-red-500 text-sm`}>{usersError}</p>
			<div>
				<Pagination
					currentPage={currentPage}
					setCurrentPage={setCurrentPage}
					totalPages={totalPages as number}
				/>
			</div>
		</div>
	);
};

export default UserTable;
